import type { Options as EditableOption } from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import { Dashboards } from '../dashboards-core';
import { DashboardMapComponent, DASHBOARD_MAP_TYPE } from './dashboard-map';
import { DashboardWidgetHost } from './dashboard-widget-host';
import { DASHBOARD_WIDGET_TYPE } from './dashboard-widget.contract';

/** The Dashboards component types this platform supports, and what each one needs. */

/** The part of a component class the authoring setup writes to. */
type EditableComponentClass = {
    defaultOptions: { editableOptions?: EditableOption[] };
};

export type DashboardComponentType = {
    /** The key Dashboards registers it under in `ComponentRegistry`. */
    readonly key: string;
    readonly componentClass: () => EditableComponentClass;
    /** Whether to offer query binding: the type reads a dataset *and* the platform offers it. */
    readonly dataBound: boolean;
    /** i18n key */
    readonly sidebarLabel?: string;
};

export const DASHBOARD_COMPONENT_TYPES = [
    {
        key: 'Highcharts',
        componentClass: () => Dashboards.HighchartsComponent,
        dataBound: true,
        sidebarLabel: 'Dashboard.EditMode.Sidebar.Chart',
    },
    {
        key: DASHBOARD_MAP_TYPE,
        componentClass: () => DashboardMapComponent,
        dataBound: true,
        sidebarLabel: 'Dashboard.EditMode.Sidebar.Map',
    },
    {
        key: DASHBOARD_WIDGET_TYPE,
        componentClass: () => DashboardWidgetHost,
        dataBound: false,
        sidebarLabel: undefined,
    },
    {
        // Kept only so old boards still load, because the `html` widget replaces every cell of this type.
        key: 'HTML',
        componentClass: () => Dashboards.HTMLComponent,
        dataBound: false,
        sidebarLabel: undefined,
    },
    {
        key: 'Navigator',
        componentClass: () => Dashboards.NavigatorComponent,
        dataBound: false,
        sidebarLabel: undefined,
    },
] as const satisfies readonly DashboardComponentType[];

/** Every component type key the platform declares. */
export type DashboardComponentKey = (typeof DASHBOARD_COMPONENT_TYPES)[number]['key'];

export const DASHBOARD_COMPONENT_KEYS: readonly DashboardComponentKey[] =
    DASHBOARD_COMPONENT_TYPES.map((type) => type.key);

export function isDashboardComponentKey(value: unknown): value is DashboardComponentKey {
    return DASHBOARD_COMPONENT_KEYS.some((key): boolean => key === value);
}

/** The types a query can be bound to. */
export function dataBoundComponentTypes(): EditableComponentClass[] {
    return DASHBOARD_COMPONENT_TYPES.filter((type) => type.dataBound).map((type) =>
        type.componentClass()
    );
}
